import { X } from "lucide-react";
import type { SimStock } from "@/hooks/useSimulator";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";

interface Props {
  ticker: string;
  stock: SimStock;
  priceHistory: Array<{ round: number; prices: Record<string, number> }>;
  hw500History: Array<{ round: number; level: number }>;
  onClose: () => void;
}

const StockChart = ({ ticker, stock, priceHistory, hw500History, onClose }: Props) => {
  const chartData = priceHistory.map((ph) => ({
    round: ph.round,
    price: ph.prices[ticker] || 0,
  }));

  const firstPrice = chartData[0]?.price || 1;
  const firstHw = hw500History[0]?.level || 1;

  const comparisonData = chartData.map((d, i) => ({
    round: d.round,
    stock: ((d.price / firstPrice) - 1) * 100,
    hw500: hw500History[i] ? ((hw500History[i].level / firstHw) - 1) * 100 : 0,
  }));

  const recent = chartData.slice(-3);
  let trend = "Sideways";
  let trendColor = "text-muted-foreground";
  if (recent.length >= 2) {
    const changes = recent.slice(1).map((d, i) => d.price - recent[i].price);
    const allUp = changes.every((c) => c > 0);
    const allDown = changes.every((c) => c < 0);
    if (allUp) { trend = "Uptrend"; trendColor = "text-emerald-400"; }
    else if (allDown) { trend = "Downtrend"; trendColor = "text-red-400"; }
  }

  const currentPrice = chartData[chartData.length - 1]?.price || 0;
  const avgPrice = chartData.reduce((s, d) => s + d.price, 0) / (chartData.length || 1);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 backdrop-blur-sm" onClick={onClose}>
      <div
        className="glass-card glow-border w-full max-w-lg mx-4 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-heading font-bold text-lg text-foreground">{ticker} — {stock.company_name}</h3>
            <p className="text-muted-foreground text-xs">{stock.sector} · Volatility: {stock.volatility_rating}/10</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1"><X className="w-5 h-5" /></button>
        </div>

        {/* Price info */}
        <div className="flex items-center gap-6 mb-4 text-xs">
          <div>
            <span className="text-muted-foreground">Price</span>
            <p className="text-foreground font-heading font-bold text-base">${currentPrice.toFixed(2)}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Avg Price</span>
            <p className="text-foreground/70 font-heading font-bold">${avgPrice.toFixed(2)}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Trend</span>
            <p className={`font-heading font-bold ${trendColor}`}>{trend}</p>
          </div>
        </div>

        {/* Price Chart */}
        <div className="mb-4">
          <p className="text-muted-foreground text-xs mb-2">Price History</p>
          <div className="h-40 bg-muted/20 rounded-xl border border-border/50 p-2">
            {chartData.length > 1 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <XAxis dataKey="round" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} axisLine={false} tickLine={false} domain={["auto", "auto"]} />
                  <Tooltip
                    contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                    labelStyle={{ color: "hsl(var(--muted-foreground))" }}
                    formatter={(v: number) => [`$${v.toFixed(2)}`, "Price"]}
                  />
                  <ReferenceLine y={avgPrice} stroke="hsl(var(--border))" strokeDasharray="3 3" />
                  <Line type="monotone" dataKey="price" stroke="hsl(195, 70%, 65%)" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground/50 text-xs">Advance rounds to see chart</div>
            )}
          </div>
        </div>

        {/* vs HW500 */}
        {comparisonData.length > 1 && (
          <div>
            <p className="text-muted-foreground text-xs mb-2">Performance vs HW500 (%)</p>
            <div className="h-32 bg-muted/20 rounded-xl border border-border/50 p-2">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={comparisonData}>
                  <XAxis dataKey="round" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                    formatter={(v: number) => [`${v.toFixed(2)}%`]}
                  />
                  <ReferenceLine y={0} stroke="hsl(var(--border))" />
                  <Line type="monotone" dataKey="stock" stroke="hsl(195, 70%, 65%)" strokeWidth={2} dot={false} name={ticker} />
                  <Line type="monotone" dataKey="hw500" stroke="hsl(var(--muted-foreground))" strokeWidth={1} dot={false} strokeDasharray="4 2" name="HW500" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockChart;
